import React, { useState } from "react";
import * as XLSX from "xlsx";
import CustomModal from "../modal";
import { Tooltip } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import DateUtils from "../../utils/date";

interface ExportCSVModalProps {
  data: any[];
  filteredData: any[];
  visibleColumns: any[];
  allColumns?: any[];
  moduleName: string;
  exportAllData?: any[];
  exportFilteredData?: any[];
  fetchExportData?: (exportType: "all" | "filtered") => Promise<any[]>;
}

const ExportToCSV: React.FC<ExportCSVModalProps> = ({
  data,
  filteredData,
  visibleColumns,
  allColumns,
  moduleName,
  exportAllData,
  exportFilteredData,
  fetchExportData,
}) => {
  const [visible, setVisible] = useState(false);
  const [exportType, setExportType] = useState<"all" | "filtered">("all");
  const [loading, setLoading] = useState(false);

  const performDownload = async () => {
    setLoading(true);
    try {
      let exportDataset =
        exportType === "filtered"
          ? (exportFilteredData ?? filteredData)
          : (exportAllData ?? data);

      if (fetchExportData) {
        exportDataset = await fetchExportData(exportType);
      }

      const columnsToUse =
        exportType === "filtered"
          ? visibleColumns
          : allColumns || visibleColumns;

      const headers = columnsToUse
        .filter((c: any) => c.key !== "actions" && c.key !== undefined)
        .map((c: any) => ({
          label: c.title || c.key,
          key: c.key || c.dataIndex,
        }));

      const extraKeys = [
        "warrantyStatus",
        "outputStatus",
        "excelStatus",
        "isActive",
      ];

      const allKeys = new Set<string>();

      headers.forEach((h) => allKeys.add(h.key));

      if (exportDataset && exportDataset.length > 0) {
        Object.keys(exportDataset[0]).forEach((key) => {
          if (extraKeys.includes(key) && !allKeys.has(key)) {
            allKeys.add(key);
          }
        });
      }

      const finalHeaders = Array.from(allKeys).map((key) => {
        const existingHeader = headers.find((h) => h.key === key);
        if (existingHeader) {
          return existingHeader;
        }

        return {
          label: key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase()),
          key,
        };
      });

      const filteredExportData = exportDataset.map((item: any) => {
        const filteredItem: any = {};
        finalHeaders.forEach((header) => {
          filteredItem[header.label] = item[header.key] ?? "-";
        });
        return filteredItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(filteredExportData, {
        cellDates: true,
      });

      Object.keys(worksheet).forEach((key) => {
        if (key.startsWith("!")) return;
        const cell = worksheet[key];
        if (cell && cell.t === "d" && cell.v instanceof Date) {
          if ((cell.v as any).__zFormat) {
            cell.z = (cell.v as any).__zFormat;
          }
        }
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(
        workbook,
        `${moduleName}_${DateUtils.format(new Date())}.xlsx`,
      );

      setVisible(false);
    } catch (error) {
      console.error("Failed to export data", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Export Excel">
        <DownloadOutlined
          onClick={() => setVisible(true)}
          style={{ cursor: "pointer" }}
        />
      </Tooltip>

      <CustomModal
        title="Download Options"
        isOpen={visible}
        onOk={performDownload}
        onCancel={() => setVisible(false)}
        okText={loading ? "Downloading..." : "Download"}
        cancelText="Cancel"
        disableOk={loading}
        cusHeader={true}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <label>
            <input
              type="radio"
              name="exportType"
              value="all"
              checked={exportType === "all"}
              onChange={(e) =>
                setExportType(e.target.value as "all" | "filtered")
              }
              disabled={loading}
            />{" "}
            Download All Records
          </label>

          <label>
            <input
              type="radio"
              name="exportType"
              value="filtered"
              checked={exportType === "filtered"}
              onChange={(e) =>
                setExportType(e.target.value as "all" | "filtered")
              }
              disabled={loading}
            />{" "}
            Download Filtered Records Only
          </label>
        </div>
      </CustomModal>
    </>
  );
};

export default ExportToCSV;
